import React, { memo, useCallback, useMemo } from 'react';

import { useSelector } from 'react-redux';

import interactionResponse from 'await-interaction-response';
import clsx from 'clsx';
import _get from 'lodash/get';
import _isEqual from 'lodash/isEqual';

// atc-ui
import { useDevice } from '@bonnet/next/device';

import { useFeatures } from '@atc/features';
import { formatNumber } from '@atc/string-fns';

import {
    Carousel,
    Chip,
} from 'reaxl';
import { sendClick } from 'reaxl-analytics';

import { suggestedFilterClick } from '@/analytics/srp/srpSearchEvents';

import srpFiltersDuck from '@/ducks/srp/srpFiltersDuck.js';
import srpSuggestedFiltersDuck from '@/ducks/srp/srpSuggestedFiltersDuck.js';

import useSrpNavigation from '@/hooks/useSrpNavigation';

import InventoryFiltersChipsContainer from './InventoryFiltersChipsContainer';

function FilterChip({ name, count, codes, onClick }) {
    return (
        <div
            data-cmp="filterChip"
            className="display-flex flex-column"
        >
            <Chip
                label={(
                    <span>
                        <span className="text-gray-base">
                            {name}
                        </span>
                        {
                            !!count && (
                                <span className="text-subdued-lightest margin-left-1">
                                    {`(${formatNumber({ value: count })})`}
                                </span>
                            )
                        }
                    </span>
                )}
                onClick={(event) => onClick(event, codes)}
                uiContext="filter"
            />
        </div>
    );
}

const ChipCarousel = memo(({
    enableBuyOnline = false,
    enableHyundaiBuyOnline = false,
    showCarousel = false,
    showFilterChips = false,
    suggestedFilters = [],
}) => {
    const device = useDevice();
    const isXs = _get(device, 'is.xs', false);
    const isLessThanMd = _get(device, 'lessThan.md', false);
    const navigateToSrp = useSrpNavigation();

    const options = useMemo(() => ({
        prevNextButtons: !isXs,
    }), [isXs]);

    const handleChipClick = useCallback(async (event, codes) => {
        const { value } = event.target;
        await interactionResponse();
        event.target.value = value;

        sendClick(suggestedFilterClick, event);

        // getExperienceFilters
        const isExperienceChip = codes && Object.values(codes).includes('BUY_ONLINE');

        let query = {};

        if (enableBuyOnline && isExperienceChip) {
            query = { experience: 'buy-online' };
        }

        // will only send query of selected chip, could include multiple filter changes
        const filtersValues = {
            ...codes,
            ...query,
        };

        await navigateToSrp({
            resetPagination: true,
            isNewSearch: true,
            filtersValues,
            action: 'merge',
        });
    }, [enableBuyOnline, navigateToSrp]);

    const carouselProps = {
        options,
        initialIndex: 0,
        lazyLoad: 3,
        fullWidth: false,
        fullHeight: true,
        fullWidthScroll: true,
        visibilityThreshold: 1,
        'data-cmp': 'suggestedFilters',
    };

    if (showCarousel) {
        return (
            <Carousel
                {...carouselProps}
            >
                <InventoryFiltersChipsContainer
                    showFallbackMessage={false}
                    showFilterChips={!isLessThanMd}
                    noWrap
                />
                {suggestedFilters.map((chip, index) => (
                    <FilterChip
                        key={`${chip?.name}-${index}`}
                        onClick={handleChipClick}
                        {...chip}
                    />
                ))}
            </Carousel>
        );
    }

    if (showFilterChips) {
        return (
            <div
                data-cmp="suggestedFilters"
            >
                <InventoryFiltersChipsContainer
                    carouselProps={carouselProps}
                    showFallbackMessage={false}
                    showFilterChips={!isLessThanMd}
                />
            </div>
        );
    }

    return null;
}, _isEqual);

const generateCpoSuggestedFilter = (count, selectedMakeCode) => {
    if (selectedMakeCode && Object.keys(selectedMakeCode).length > 0) {
        const selectedMakeEntries = Object.entries(selectedMakeCode);
        if (selectedMakeEntries.length === 1) {
            const [, selectedLabel] = selectedMakeEntries[0];
            return {
                name: `${selectedLabel} Certified Pre-Owned`,
                count,
                codes: {
                    listingType: 'CERTIFIED',
                },
            };
        }
        return {
            name: 'Manufacturer Certified',
            count,
            codes: {
                listingType: 'CERTIFIED',
            },
        };
    }
    return {};
};

export function SuggestedFilterContainer() {
    const {
        hyundai_buy_online: [enableHyundaiBuyOnline],
        srp_buy_online: [enableBuyOnline],
        suggested_filters: [enableSuggestedFilters],
        oem_cpo_srp: [, { suggested_filter: enableCpoSuggestedFilter }],
    } = useFeatures([
        'hyundai_buy_online',
        'srp_buy_online',
        'suggested_filters',
        'oem_cpo_srp',
    ]);
    const suggestedFilters = useSelector(srpSuggestedFiltersDuck.selectors.getSuggestedFilters) || [];
    const isBuyOnlineSelected = useSelector(srpFiltersDuck.selectors.isBuyOnlineSelected);
    const buyOnlineOptions = useSelector(srpFiltersDuck.selectors.getBuyOnlineFilterOptions);
    const cpoSelected = useSelector(srpFiltersDuck.selectors.isCpoSelected);
    const copOptions = useSelector(srpFiltersDuck.selectors.getCpoFilterOptions);
    const { makeCode: selectedMakeCode } = useSelector(srpFiltersDuck.selectors.getFilterValueLabels);
    const customFilters = [];
    if (!enableHyundaiBuyOnline && enableBuyOnline && !isBuyOnlineSelected && buyOnlineOptions?.count) {
        customFilters.push(
            {
                name: 'Purchase Online',
                count: buyOnlineOptions?.count,
                codes: {
                    homeServices: 'BUY_ONLINE',
                },
            },
        );
    }
    if (enableCpoSuggestedFilter && !cpoSelected && copOptions?.count && selectedMakeCode) {
        const cpoFilters = generateCpoSuggestedFilter(copOptions.count, selectedMakeCode);
        if (Object.keys(cpoFilters).length > 0) {
            customFilters.push(cpoFilters);
        }
    }
    const combinedFilters = [...customFilters, ...suggestedFilters];
    const updatedFilters = customFilters.length > 0 ? combinedFilters : suggestedFilters;

    const showSuggestedFilters = updatedFilters.length > 0;
    const activeFilterValues = useSelector(srpFiltersDuck.selectors.getSelectedFilterChips, _isEqual);
    const showFilterChips = activeFilterValues && Object.keys(activeFilterValues).length > 0;

    const rootClassNames = clsx('flex-1', {
        'margin-vertical-1': !showSuggestedFilters && !showFilterChips,
    });

    return enableSuggestedFilters && (
        <div className={rootClassNames}>
            <ChipCarousel
                enableHyundaiBuyOnline={enableHyundaiBuyOnline}
                enableBuyOnline={enableBuyOnline}
                showCarousel={!!showSuggestedFilters}
                showFilterChips={!!showFilterChips}
                suggestedFilters={updatedFilters}
            />
        </div>
    );
}

export default React.memo(SuggestedFilterContainer);
